import React, { useState } from 'react';
import { BringzzButton, BringzzText, BringzzImage, BringzzModal } from "@bringzz/components";
import { useNavigation } from 'context/NavigationContext';
import {
    BringzzPageContainer,
    BringzzIcon,
} from "@bringzz/components";
import { toast } from 'react-toastify';

// Mock data for saved payment methods
const MOCK_PAYMENT_METHODS = [
    {
        id: 1,
        type: 'credit_card',
        last4: '4242',
        expiryDate: '12/24',
        brand: 'Visa'
    },
    {
        id: 2,
        type: 'sofort',
        bankName: 'Deutsche Bank'
    }
];

const ConsumerPaymentSettingsPage = () => {
    const { backPress, navigate } = useNavigation();
    const [savedMethods, setSavedMethods] = useState(MOCK_PAYMENT_METHODS);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedMethodId, setSelectedMethodId] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const handleDeleteMethod = async () => {
        if (!selectedMethodId) return;

        try {
            setDeleteLoading(true);
            // Simulate API call
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Remove from local state
            const newMethods = savedMethods.filter(method => method.id !== selectedMethodId);
            setSavedMethods(newMethods);
            setModalOpen(false);
            toast.success('Payment method deleted successfully');
        } catch (error) {
            console.error('Failed to delete payment method:', error);
            toast.error('Failed to delete payment method');
        } finally {
            setDeleteLoading(false);
            setSelectedMethodId(null);
        }
    };

    const renderPaymentMethodDetails = (method) => {
        switch (method.type) {
            case 'credit_card':
                return (
                    <>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 flex items-center justify-center bg-magic-lilac/10 rounded-full">
                                <BringzzIcon
                                    icon="IconCreditCard"
                                    folder="LineIcons"
                                    size="24"
                                    className="text-magic-lilac"
                                />
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-[#1C1B1F]">
                                    {method.brand} •••• {method.last4}
                                </p>
                                <p className="text-xs text-[#49454F]">
                                    Expires {method.expiryDate}
                                </p>
                            </div>
                        </div>
                    </>
                );
            case 'sofort':
                return (
                    <>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 flex items-center justify-center bg-magic-lilac/10 rounded-full">
                                <BringzzIcon
                                    icon="IconBank"
                                    folder="LineIcons"
                                    size="24"
                                    className="text-magic-lilac"
                                />
                            </div>
                            <div>
                                <p className="text-sm font-semibold text-[#1C1B1F]">
                                    Sofort
                                </p>
                                <p className="text-xs text-[#49454F]">
                                    {method.bankName}
                                </p>
                            </div>
                        </div>
                    </>
                );
            default:
                return null;
        }
    };

    return (
        <BringzzPageContainer
            className="bg-white"
            header={{
                left: (
                    <BringzzIcon
                        size='22'
                        icon='IconChevronLeft'
                        folder='LineIcons'
                        className='cursor-pointer'
                        onClick={backPress}
                    />
                ),
                title: <BringzzText tag='h2'>Payment Methods</BringzzText>
            }}
            footer={
                <div className='pb-28 px-6'>
                    <button
                        className="w-full py-4 text-sm font-medium text-midnight-blue bg-magic-lilac rounded-full disabled:opacity-50"
                        onClick={() => navigate('/payment/add')}
                    >
                        <BringzzText
                            tag='h3'
                            className='text-normal whitespace-pre-line truncate'
                        >
                            Add new payment method
                        </BringzzText>
                    </button>
                </div>
            }
        >
            <div className="space-y-4 p-6">
                {savedMethods.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <BringzzText tag='p'>No payment methods found</BringzzText>
                    </div>
                ) : (
                    savedMethods.map((method) => (
                        <div key={method.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-magic-lilac/30 transition-all duration-200">
                            <div className="flex items-center justify-between p-4 relative">
                                {renderPaymentMethodDetails(method)}
                                <div className="flex items-center gap-2">
                                    <div
                                        className="flex items-center justify-center w-10 h-10 hover:bg-magic-lilac/5 transition-colors duration-200 cursor-pointer rounded-lg"
                                        onClick={() => navigate(`/payment/edit/${method.id}`)}
                                    >
                                        <BringzzIcon
                                            size='20'
                                            icon='IconPencilCopy'
                                            folder='LineIcons'
                                            className='text-magic-lilac'
                                        />
                                    </div>
                                    <div
                                        className="flex items-center justify-center w-10 h-10 hover:bg-red-50 transition-colors duration-200 cursor-pointer rounded-lg"
                                        onClick={() => {
                                            setSelectedMethodId(method.id);
                                            setModalOpen(true);
                                        }}
                                    >
                                        <BringzzIcon
                                            size='20'
                                            icon='IconTrashCanCopy'
                                            folder='LineIcons'
                                            className='text-red-500'
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {modalOpen && (
                <BringzzModal
                    title={'Delete this payment method?'}
                    description={`This payment method will be permanently deleted.`}
                    onCancel={() => setModalOpen(false)}
                    confirmButtonText={deleteLoading ? 'Deleting...' : 'Delete'}
                    onConfirm={handleDeleteMethod}
                    disabled={deleteLoading}
                />
            )}
        </BringzzPageContainer>
    );
};

export default ConsumerPaymentSettingsPage;
